import { useNavigate } from "react-router-dom";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  SxProps,
  TableRow,
  Typography,
  TextField,
  Tooltip,
  CircularProgress,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@mui/material";
import { SuccessAlertContext } from "App";
import DeleteIcon from "@mui/icons-material/Delete";
import Notify from "config/notify";
import ERROR_MESSAGE from "constants/message";
import { checkAccessTokenExpired, getUserType } from "util/auth";
import { HelpOutline } from "@mui/icons-material";
import { WarehouseInfo, NewWarehouseInfo, UpdateWarehouseRequest } from "../types";
import Surround from "../components/Surround";
import WarehouseTable from "../components/WarehouseTable";
import TableCellSmall, {
  TableCellWide,
  TableHeaderCellSmall,
  TableHeaderCellWide,
} from "../components/TableCellSmall";
import useWarehouseRegister from "../hooks/useWarehouseRegister";
import NewRegisterDialog from "../components/NewRegisterDialog";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import PrefEditModal from "../components/PrefEditDialog";
import downloadHelpPdf from "../api/downloadHelpPdf";

const styles: { [key: string]: SxProps } = {
  supplierTopCol: {
    textAlign: "center",
    p: 0.5,
    borderBottom: "none",
  },
  supplierBottomCol: {
    textAlign: "center",
    p: 0.5,
    top: 58,
  },
  buttonContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  leftButtonContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: 2,
  },
  radioGroupContainer: {
    display: "flex",
    justifyContent: "flex-end",
  },
};

type Props = { onBack: Function };

/**
 * 仕入先用近隣・センター倉庫登録画面
 */
export default function WarehouseRegister() {
  const { showAlert } = useContext(SuccessAlertContext);
  // 近隣倉庫orセンター倉庫
  const [filteredType, setFilteredType] = useState<"近隣倉庫" | "センター倉庫">("センター倉庫");
  // hookからデータの取得
  const [
    {
      total, // 総数
      warehouseList, // 現在の倉庫情報リスト
      isLoading: isWarehouseLoading, // ローディング状態
    },
    {
      fetchWarehouses, // 取得関数
      registerWarehouse, // 登録関数
      updateWarehouse, // 更新関数
      deleteWarehouse, // 削除関数
    },
  ] = useWarehouseRegister(showAlert);

  const [isOpenNewRegister, setIsOpenNewRegister] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [warehouseToDelete, setWarehouseToDelete] = useState<WarehouseInfo | null>(null);
  const [openEditModal, setOpenEditModal] = useState(false); // 更新モーダルの開閉状態
  const [selectedWarehouse, setSelectedWarehouse] = useState<WarehouseInfo | null>(null); // 現在選択されている倉庫

  // 登録済みの都道府県を取得
  const registeredPrefectures = warehouseList.flatMap((warehouse) =>
    warehouse.pref_infos.map((pref) => pref.pref_cd),
  );

  // 登録済みの都道府県から選択された倉庫情報の都道府県を除外
  const filteredRegisteredPrefectures = selectedWarehouse
    ? registeredPrefectures.filter(
        (pref_cd) => !selectedWarehouse.pref_infos.some((pref) => pref.pref_cd === pref_cd),
      )
    : registeredPrefectures; // 選択されている倉庫がない場合はすべて保持

  const navigate = useNavigate();

  const handleBack = () => navigate(-1);

  // 初期レンダリング時にAPI呼び出し
  useEffect(() => {
    fetchWarehouses({ depot_kind: 0 });
  }, []); // 最初のレンダリング時に一度実行

  // 倉庫タイプを変更時にAPI呼び出し
  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newType = event.target.value as "近隣倉庫" | "センター倉庫";
    setFilteredType(newType);
    // 新しいタイプに基づいて再取得
    fetchWarehouses({ depot_kind: newType === "近隣倉庫" ? 1 : 0 });
  };

  // 新規登録
  const handleRegisterWarehouse = (newWarehouse: NewWarehouseInfo) => {
    registerWarehouse({
      depot_kind: newWarehouse.depot_kind,
      operation: 0, // 登録
      depot_name: newWarehouse.depot_name,
      pref_infos: newWarehouse.pref_infos,
    });
  };

  // 更新処理
  const handleEditOpenModal = (warehouse: WarehouseInfo) => {
    setSelectedWarehouse(warehouse);
    setOpenEditModal(true); // モーダルを開く
  };

  const handleEditCloseModal = () => {
    setOpenEditModal(false); // モーダルを閉じる
    setSelectedWarehouse(null);
  };

  // 更新処理
  const handleUpdateWarehouse = (updatedWarehouse: UpdateWarehouseRequest) => {
    updateWarehouse({
      depot_kind: updatedWarehouse.depot_kind,
      operation: 1, // 更新
      depot_cd: updatedWarehouse.depot_cd,
      depot_name: updatedWarehouse.depot_name,
      pref_infos: updatedWarehouse.pref_infos,
    });
  };

  // 削除処理
  const handleDeleteIconClick = (warehouse: WarehouseInfo) => {
    setWarehouseToDelete(warehouse); // 削除対象の倉庫を設定
    setConfirmDeleteOpen(true); // ダイアログを開く
  };

  const handleCloseConfirmDelete = () => {
    setConfirmDeleteOpen(false); // ダイアログを閉じる
    setWarehouseToDelete(null); // 削除対象の倉庫をクリア
  };

  const handleDelete = async () => {
    if (warehouseToDelete) {
      await deleteWarehouse(warehouseToDelete.depot_kind,warehouseToDelete.depot_cd); // 倉庫を削除
      // console.log("倉庫削除種別：",warehouseToDelete.depot_kind)
      handleCloseConfirmDelete(); // ダイアログを閉じる
    }
  };

  const tableHeadElement = () => (
    <TableRow>
      <TableHeaderCellSmall sx={{ maxWidth: 1000, width: 300 }}>倉庫コード</TableHeaderCellSmall>
      <TableHeaderCellSmall sx={{ maxWidth: 1000, width: 600 }}>倉庫名</TableHeaderCellSmall>
      <TableHeaderCellWide sx={{ maxWidth: 1500, width: 1500 }}>都道府県</TableHeaderCellWide>
      <TableHeaderCellSmall
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          maxWidth: 1000,
          width: 100,
        }}
      >
        削除ボタン
      </TableHeaderCellSmall>
    </TableRow>
  );

  const fieldCell = (warehouse: WarehouseInfo) => (
    <TableRow key={warehouse.depot_cd}>
      <TableCellSmall sx={{ maxWidth: 1000 }}>{warehouse.depot_cd}</TableCellSmall>
      <TableCellSmall sx={{ maxWidth: 1000 }}>{warehouse.depot_name}</TableCellSmall>
      <TableCellWide sx={{ maxWidth: 1000 }}>
        <Tooltip title={warehouse.pref_infos.map((pref) => pref.pref_name).join(", ") || "なし"}>
          <TextField
            variant="outlined"
            label="都道府県名"
            value={warehouse.pref_infos.map((pref) => pref.pref_name).join(", ") || ""}
            size="small"
            fullWidth
            InputProps={{
              readOnly: true,
            }}
            onClick={() => handleEditOpenModal(warehouse)} // クリックでモーダルを開く
            sx={{ minWidth: "100%", whiteSpace: "normal" }}
          />
        </Tooltip>
      </TableCellWide>
      <TableCellSmall
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          maxWidth: 1000,
        }}
      >
        <DeleteIcon onClick={() => handleDeleteIconClick(warehouse)} />
      </TableCellSmall>
    </TableRow>
  );

  const tableBodyElement = useCallback(() => {
    if (warehouseList.length === 0) {
      return null; // データがない場合はnullを返す
    }

    return <>{warehouseList.map((warehouse) => fieldCell(warehouse))}</>;
  }, [warehouseList, fieldCell]);

  useEffect(() => {
    setTimeout(() => {
      (async () => {
        const userType = localStorage.getItem("user_type");
        if (!(userType === "C0004")) {
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: "仕入先ユーザーで再度ログインしてください。",
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        } else if (checkAccessTokenExpired()) {
          // 期限切れなら
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: ERROR_MESSAGE.UNAUTHORIZED.MESSAGE,
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        }
      })();
    }, 0);
  }, []);

  return (
    <Box width="90%" alignSelf="center" marginLeft="5%" minHeight={330} position="relative" pt={3}>
      <Box width="100%" height={32} textAlign="right" position="absolute" top={0}>
        <Button variant="custom-outlined" onClick={async (e) => {
          const url = await downloadHelpPdf(`${getUserType()}/resources/stocks/manual_supplier_warehouse.pdf`);
          window.open(url, "_blank", "noopener,noreferrer");
        }} startIcon={<HelpOutline color="secondary" />} sx={{height: 24, padding: 1, marginTop: 0.5, marginRight: 1}}>ヘルプ</Button>
      </Box>
      <Box>
        <Box position="absolute" top={32} left={0} width="100%" textAlign="center">
          <Typography fontSize={22}>
            {filteredType === "近隣倉庫" ? "近隣倉庫登録" : "センター倉庫登録"}
          </Typography>
        </Box>

        <Box sx={styles.buttonContainer}>
          <Box sx={styles.leftButtonContainer}>
            <Box mx={1}>
              <Button onClick={handleBack} variant="custom-outlined">
                <Box>＜ 戻る</Box>
              </Button>
            </Box>
          </Box>

          <Box mx={1}>
            <Button onClick={() => setIsOpenNewRegister(true)} variant="contained">
              <Box>新規登録</Box>
            </Button>
          </Box>
        </Box>

        <Box sx={styles.buttonContainer}>
          <Box sx={styles.radioGroupContainer}>
            <Surround title="表示する倉庫を選択" sx={{ p: 1 }}>
              <RadioGroup
                row
                value={filteredType}
                onChange={handleTypeChange}
                aria-label="warehouse type"
              >
                <FormControlLabel
                  value="センター倉庫"
                  control={<Radio color="primary" />}
                  label="センター倉庫"
                />
                <FormControlLabel
                  value="近隣倉庫"
                  control={<Radio color="primary" />}
                  label="近隣倉庫"
                />
              </RadioGroup>
            </Surround>
          </Box>
        </Box>

        {!isWarehouseLoading && (
          <WarehouseTable
            searchLen={total}
            tableHeadElement={tableHeadElement}
            tableBodyElement={tableBodyElement}
          />
        )}
        {isWarehouseLoading && (
          <Box height={330} textAlign="center" alignContent="center">
            <CircularProgress />
          </Box>
        )}
      </Box>

      {/* 新規登録 */}
      <NewRegisterDialog
        open={isOpenNewRegister}
        onClose={setIsOpenNewRegister}
        onRegister={handleRegisterWarehouse}
        filteredType={filteredType}
        registeredPrefectures={registeredPrefectures}
      />

      {/* 都道府県更新 */}
      <PrefEditModal
        open={openEditModal}
        onClose={handleEditCloseModal}
        warehouseInfo={selectedWarehouse}
        onUpdateWarehouse={handleUpdateWarehouse}
        registeredPrefectures={filteredRegisteredPrefectures}
        filteredType={filteredType}
      />

      {/* 削除確認 */}
      <DeleteConfirmDialog
        open={confirmDeleteOpen}
        onClose={handleCloseConfirmDelete}
        onConfirm={handleDelete}
        warehouseToDelete={warehouseToDelete}
      />
    </Box>
  );
}
